import React, { useState, useMemo, useCallback, useEffect, MutableRefObject } from 'react';
import { useApolloClient, gql, useQuery } from '@apollo/client';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import reduce from 'lodash/reduce';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';

import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useSelector, useBridgeApi, useLogger } from '../../Hooks';
import useVenueManager from 'Hooks/useVenueManager';
import View from './view';
import useI18n from '../../i18n';
import { useQuantity } from '../../Hooks/ItemDetails';
import { useVariations } from '../../Components/Variations';
import {
  GetItem,
  GetItem_item_upsales,
  GetItem_item_appliedVariations,
  GetItem_item_defaultVariations,
  currentItemFragment,
  currentItemFragment_image,
  currentItemFragment_titleLang,
  currentItemFragment_descriptionLang,
  GetItemVariables,
} from './__queries__';
import { ITEM_QUERY } from './getEntryCheckoutQuery';
import { useStorage } from 'Components/Storage';
import { useShoppingCartApi, useShoppingCart } from 'Components/ShoppingCartUniverse';
import useEntryFinder from 'Hooks/useEntryFinder';
import { checkVariationOutOfStock } from 'utils/inventory';

import { useConfig } from 'Components/ConfigProvider';

import { ALLERGEN } from '../../../__queries__';


export const SCENES = {
  ITEM: 'ITEM',
  UPSALES: 'UPSALES',
};

export type GroupsSelectedIds = {
  [key: string]: string[];
};

interface LightEntry {
  id: string;
  price: number;
  item: {
    id: string;
    image: currentItemFragment_image;
    titleLang: currentItemFragment_titleLang;
    descriptionLang: currentItemFragment_descriptionLang;
    upsales?: GetItem_item_upsales[];
    appliedVariations?: GetItem_item_appliedVariations[];
    defaultVariations?: GetItem_item_defaultVariations[];
    allergens: ALLERGEN[];
  };
}

interface Props {
  /**
   * changeTitle
   */
  changeTitle?: (text: string) => void;
  /**
   * changeOnBackFunc
   */
  changeOnBackFunc?: (callback: (() => void) | undefined) => void;
  /**
   * changeShowOnScroll
   */
  changeShowOnScroll?: (scrollSize: number | false) => void;
  /**
   * contentRef
   */
  contentRef?: MutableRefObject<HTMLDivElement>;
}

/**
 * Item scene
 */
const Item = ({ changeTitle, changeOnBackFunc, changeShowOnScroll, contentRef }: Props) => {
  const config = useConfig();
  const logger = useLogger('item');
  const [storageData] = useStorage();
  const params = useParams<{ item_id: string }>();
  const venueParams = useParams<{ venue_id?: string }>();
  const venueId = venueParams?.venue_id || '';
  const location = useLocation();
  const history = useHistory();
  const shoppingCart = useShoppingCart();
  const client = useApolloClient();
  const findEntryFromItem = useEntryFinder();
  const { i18n } = useI18n();
  useVenueManager();
  const api = useBridgeApi();
  const shoppingCartApi = useShoppingCartApi();
  const venue = useSelector((state) => state.venue);
  const inventory = useSelector((state) => state.inventory);
  const cartId = useSelector((state) => state.cartId);
  const qrOrder = useSelector((state) => state.qrOrder);
  const authToken = storageData.authToken;
  const [quantityItems, setQuantityItems] = useQuantity('1');
  const [selectedUpsalesIds, setSelectedUpsalesIds] = useState<GroupsSelectedIds | {}>({});
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [activeScene, setActiveScene] = useState(SCENES.ITEM);
  const [upsalesStep, setUpsalesStep] = useState(0);
  const isLoggedIn = !!authToken;
  const itemId = params?.item_id || '';

  useEffect(() => {
    if (!contentRef?.current?.scrollTo) {
      return;
    }
    contentRef.current.scrollTo({ top: 0 });
  }, [contentRef, activeScene]);

  const { data, loading, error: dataError } = useQuery<GetItem, GetItemVariables>(ITEM_QUERY, {
    variables: {
      id: itemId,
    },
  });

  const entry = findEntryFromItem(itemId)!;

  const currentItem = useMemo(
    () =>
      client.readFragment<currentItemFragment>({
        id: itemId,
        fragment: gql`
          fragment currentItemFragment on Item {
            id
            image {
              id
              file {
                id
                url
              }
            }
            titleLang {
              id
              se
              en
              no
            }
            descriptionLang {
              id
              se
              en
              no
            }
          }
        `,
      }),
    [client, itemId, data, entry],
  );

  const lightEntry: LightEntry | null = useMemo(() => {
    if (entry && currentItem) {
      return {
        id: entry.id,
        price: entry.price,
        item: {
          id: currentItem.id,
          image: currentItem.image,
          titleLang: currentItem.titleLang,
          descriptionLang: currentItem.descriptionLang,
          defaultVariation: [],
          appliedVariations: [],
          allergens: [],
        },
      };
    }
    return null;
  }, [entry, data, currentItem]);

  const handleSelectUpsellId = useCallback(
    (id: string, groupId: string) => {
      let value = [];
      const upsalesIds = get(selectedUpsalesIds, groupId, []);
      if (includes(upsalesIds, id)) {
        value = filter(upsalesIds, (variationId) => variationId !== id);
      } else {
        value = [...upsalesIds, id];
      }

      setSelectedUpsalesIds({
        ...selectedUpsalesIds,
        [groupId]: value,
      });
    },
    [selectedUpsalesIds],
  );

  const onBack = () => {
    if (upsalesStep === 0) {
      return setActiveScene(SCENES.ITEM);
    }
    if (upsalesStep > 0 && upsalesStep < upsales.length) {
      return setUpsalesStep((prev) => prev - 1);
    }
  };

  useEffect(() => {
    const itemScene = activeScene === SCENES.ITEM;
    if (changeOnBackFunc) {
      changeOnBackFunc(itemScene ? undefined : onBack);
    }
    if (changeShowOnScroll) {
      changeShowOnScroll(itemScene ? 50 : false);
    }
  }, [activeScene, upsalesStep]);

  const handleChangeQuantity = (quantity: string) => {
    api.vibrate('impactLight');
    setQuantityItems(quantity);
  };

  const handleItemButton = () => {
    // Allow "Anonymous Purchase" check.
    if (!config.ANONYMOUS_PURCHASE && !isLoggedIn && (!qrOrder || !storageData.qrOrder)) {
      // If restaurant doesn't allow anonymous purchases & the user is not logged in, show login modal (Unless it's table ordering).
      setShowLoginModal(true);
      return;
    }

    const showUpsales = upsales.length > 0;

    if (showUpsales) {
      setActiveScene(SCENES.UPSALES);
    } else {
      handleAddToCart();
    }
  };

  const handleUpsalesButton = () => {
    handleAddToCart();
  };

  const itemData = data ?? lightEntry;

  const image = itemData?.item?.image || null;
  const titleLang = itemData?.item?.titleLang || null;
  const descriptionLang = itemData?.item?.descriptionLang || null;
  const appliedVariations = itemData?.item?.appliedVariations || [];
  const defaultVariations = itemData?.item?.defaultVariations || [];

  const upsales = itemData?.item?.upsales || [];
  const allergens = itemData?.item?.allergens || [];

  // if a defualt variation is out of stock, add it to initial selection
  const outOfStockVariations = defaultVariations.filter((v) =>
    checkVariationOutOfStock(v.asItem?.id || v.id, inventory),
  );
  const [groups, onSelectVariation, selectedVariations, removedVariations] = useVariations({
    defaultVariations: defaultVariations.map((v) => v.id),
    appliedVariations: appliedVariations.map((av) => av.id),
    initialSelection: [...filter(defaultVariations, 'group'), ...outOfStockVariations].map(
      (v) => v.id,
    ),
  });

  const checkRequiredVariations = useCallback(() => {
    const disabled = groups.some((group) => !group.valid);

    if (!disabled) {
      return true;
    }

    const requiredVariationsGroup = groups.filter((g) => g.required);
    const notValidVariation = requiredVariationsGroup.find((g) => !g.valid);

    if (!notValidVariation) {
      return true;
    }

    return false;
  }, [groups]);

  const getTotal = useCallback(() => {
    if (!entry) {
      return 0;
    }

    const quantity = parseInt(quantityItems, 10) || 1;

    const variationsPrice = reduce(
      groups,
      (result, currentGroup) => {
        const selected = currentGroup.variations
          .filter((v) => v.selected)
          .filter((v) => defaultVariations.every((d) => d.id !== v.id));
        const sum = reduce(selected, (acc, val) => acc + val.price, 0);

        return result + sum;
      },
      0,
    );

    let total = entry.price;

    total += variationsPrice;

    return total * quantity;
  }, [quantityItems, data, entry, lightEntry, selectedVariations, findEntryFromItem]);

  const handleAddToCart = () => {
    if (!data || !shoppingCart) {
      return;
    }

    const quantity = parseInt(quantityItems, 10);
    const totalPerItem = getTotal() / quantity;

    const selectedVariationsList = groups.reduce(
      (arr, group) => {
        group.variations.forEach((variation) => {
          if (variation.selected && !variation.isDefault) {
            arr.push({
              id: variation.id,
              amount: variation.price,
              title: variation.name,
              groupId: group.id,
            });
          }
        });
        return arr;
      },
      [] as Array<{
        id: string;
        amount: number;
        title: string;
        groupId: string;
      }>,
    );

    const removedVariationsList = removedVariations.map((v) => ({
      id: v.id,
      title: i18n(v.name),
    }));

    const items: Array<{
      title: string;
      itemId: string;
      entryId: string;
      variations: {
        id: string;
        amount: number;
        title: string;
        groupId: string;
      }[];
      removedVariations: string[];
      amount: number;
      disableMembershipDiscounts: boolean;
      discount: number;
      vatRate?: number | undefined;
    }> = [];

    const itemToAdd = {
      title: data.item.titleLang.se,
      itemId,
      entryId: entry.id,
      variations: selectedVariationsList,
      removedVariations: removedVariationsList.map((r) => r.id),
      amount: totalPerItem,
      disableMembershipDiscounts: data.item.disableMembershipDiscount,
      discount: 0,
      vatRate: data.item.vatRate,
    };

    if (quantity > 1) {
      for (let i = 0; i < quantity; i += 1) {
        items.push(itemToAdd);
      }
      logger.info('Items added to cart', {
        cartId,
        quantity,
        entryId: items[0].entryId,
        itemId: items[0].itemId,
        name: items[0].title,
      });
    } else if (quantity === 1) {
      items.push(itemToAdd);
      logger.info('Item added to cart', {
        cartId,
        entryId: entry.id,
        itemId,
        name: data.item.titleLang.se,
      });
    }

    const upsaleIds = flatMap(selectedUpsalesIds);

    if (upsaleIds.length > 0) {
      const allUpsaleItems = data.item.upsales.flatMap((u) => u.items);

      upsaleIds.forEach((id: string) => {
        const upsaleEntry = findEntryFromItem(id);
        const item = allUpsaleItems.find((item) => item.id === id);

        if (!upsaleEntry || !item) {
          console.error('No entry for item');
          return;
        }

        items.push({
          amount: upsaleEntry.price,
          disableMembershipDiscounts: item.disableMembershipDiscount,
          discount: 0,
          entryId: upsaleEntry.id,
          itemId: item.id,
          removedVariations: [],
          title: i18n(item.titleLang),
          variations: [],
          vatRate: item.vatRate,
        });
      });
    }

    shoppingCartApi.addItems(items);

    api.vibrate('selection');

    if (venue && shoppingCart) {
      history.push(`/order/${venueId}${location.pathname.includes('menu') ? '/menu' : ''}`);
    }
  };

  const total = getTotal();

  useEffect(() => {
    if (changeTitle) {
      changeTitle(i18n(titleLang));
    }

    if (changeShowOnScroll) {
      changeShowOnScroll(activeScene === SCENES.ITEM ? 50 : false);
    }
  }, [changeTitle, changeShowOnScroll, titleLang, activeScene]);

  const allRequiredVariationsSelected = checkRequiredVariations();

  return (
    <View
      activeScene={activeScene}
      itemImage={image?.file?.url}
      itemTitle={i18n(titleLang)}
      itemDescription={i18n(descriptionLang)}
      allRequiredVariationsSelected={allRequiredVariationsSelected}
      isLoggedIn={isLoggedIn}
      loading={loading}
      onItemButton={handleItemButton}
      quantity={quantityItems}
      onChangeQuantity={handleChangeQuantity}
      error={dataError}
      showLoginModal={showLoginModal}
      setShowLoginModal={setShowLoginModal}
      allergens={allergens}
      variationGroups={groups}
      onSelectVariationId={onSelectVariation}
      upsales={upsales}
      selectedUpsalesIds={selectedUpsalesIds}
      onSelectUpsellId={handleSelectUpsellId}
      upsalesStep={upsalesStep}
      setUpsalesStep={setUpsalesStep}
      onUpsalesButton={handleUpsalesButton}
      total={total}
    />
  );
};

export default Item;
